import { useCallback } from 'react';
import { logger } from '../../../shared/utils/logger';
import { MessageSanitizer } from '../utils/sanitizer';

type NotificationType = 'success' | 'error' | 'info' | 'warning';

export const useChatNotifications = () => {
  
  /**
   * Показать уведомление
   */
  const showNotification = useCallback((message: string, type: NotificationType = 'info') => {
    logger.debug(`🔔 Уведомление (${type}):`, message);
    
    // Создаем уведомление
    const notification = document.createElement('div');
    notification.className = `
      fixed top-4 right-4 z-[200] max-w-sm p-4 rounded-lg shadow-lg transition-all duration-300 transform translate-x-full
      ${type === 'success' ? 'bg-green-500 text-white' : ''}
      ${type === 'error' ? 'bg-red-500 text-white' : ''}
      ${type === 'warning' ? 'bg-yellow-500 text-white' : ''}
      ${type === 'info' ? 'bg-blue-500 text-white' : ''}
    `;
    
    notification.innerHTML = `
      <div class="flex items-center space-x-2">
        <div class="flex-shrink-0">
          ${type === 'success' ? '✅' : ''}
          ${type === 'error' ? '❌' : ''}
          ${type === 'warning' ? '⚠️' : ''}
          ${type === 'info' ? 'ℹ️' : ''}
        </div>
        <div class="flex-1 text-sm font-medium">${MessageSanitizer.sanitizeText(message)}</div>
        <button class="flex-shrink-0 text-white hover:text-gray-200" onclick="this.parentElement.parentElement.remove()">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Анимация появления
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 10);
    
    // Автоматическое скрытие через 5 секунд
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 300);
    }, 5000);
  }, []);

  /**
   * Воспроизведение звука уведомления
   */
  const playSound = useCallback(() => {
    try {
      // Создаем простой звук уведомления
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
      
      logger.debug('🔊 Звук уведомления воспроизведен');
    } catch (error) {
      logger.debug('🔇 Не удалось воспроизвести звук:', error);
    }
  }, []);

  /**
   * Показать desktop уведомление (если разрешено)
   */
  const showDesktopNotification = useCallback((title: string, body: string, icon?: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: icon || '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'chat-notification',
        // renotify: true // Не поддерживается в TypeScript типах
      });
      
      // Автоматически закрываем через 5 секунд
      setTimeout(() => {
        notification.close();
      }, 5000);
      
      logger.debug('🖥️ Desktop уведомление показано:', title);
    }
  }, []);

  /**
   * Запрос разрешения на desktop уведомления
   */
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        logger.debug('🔔 Разрешение на уведомления:', permission);
        return permission === 'granted';
      } catch (error) {
        logger.error('❌ Ошибка запроса разрешения на уведомления:', error);
        return false;
      }
    }
    return Notification.permission === 'granted';
  }, []);

  return {
    showNotification,
    playSound,
    showDesktopNotification,
    requestNotificationPermission
  };
};
